import type { EditorDefns } from "../utility";
/**
 * Type definition for column configuration in the Scaffold, including optional custom cell component and width.
 */
export interface ColScaffold<T> {
  /** Column id */
  id: string;
  /** Optional custom cell component for the column */
  cellComponent?: EditorDefns<T>;
  /** Column number */
  colNumber: number;
  /** Decimal alignment percentage for the column */
  decimalAlignPercent?: number;
  /** Stop column from being resizable */
  noResize?: boolean;
  /** Column width */
  width: number;
}

/**
 * Type definition for row configuration in the Scaffold, including optional custom cell component, height, and alignment.
 */
export interface RowScaffold<T> {
  /** Row id */
  id: string;
  /** Optional custom cell component for the row */
  cellComponent?: EditorDefns<T>;
  /** Row height */
  height: number;
  /** Row group number */
  rowGroupNumber: number;
  /** Row number */
  rowNumber: number;
  /** Row type */
  type: "header" | "body" | "footer";
  /** Text alignment for the row */
  textAlignment?: "left" | "center" | "right" | "decimal";
}

/**
 * Character enum representing scaffold cell drop zones.
 * The rendered overlay uses sixteen zones arranged as a 4×4 grid:
 * corners, split edge zones, and four centre quadrants.
 */
export enum ScaffoldDropZone {
  TopLeft = "TL",
  TopEdgeLeft = "TEL",
  TopEdgeRight = "TER",
  TopRight = "TR",
  LeftEdgeTop = "LET",
  CentreTopLeft = "CTL",
  CentreTopRight = "CTR",
  RightEdgeTop = "RET",
  LeftEdgeBottom = "LEB",
  CentreBottomLeft = "CBL",
  CentreBottomRight = "CBR",
  RightEdgeBottom = "REB",
  BottomLeft = "BL",
  BottomEdgeLeft = "BEL",
  BottomEdgeRight = "BER",
  BottomRight = "BR",
}

/**
 * Parameters provided when resolving drop-zone highlighting via function-based zone colours.
 */
export interface IScaffoldZoneColorResolverParams {
  /** Identifier of the row currently being evaluated. */
  rowId: string;
  /** Identifier of the column currently being evaluated. */
  colId: string;
  /** Row number of the active row, when available. */
  rowNumber?: number;
  /** Column number of the active column, when available. */
  colNumber?: number;
  /** Primary zone currently being evaluated. */
  zone: ScaffoldDropZone;
}

/**
 * Result returned by function-based zone colour resolution.
 */
export interface IScaffoldZoneColorResolverResult {
  /**
   * Highlight colour for the evaluated zone.
   * - string: apply the supplied colour and allow drop
   * - undefined: no colour highlight and allow drop
   * - null: drop is not allowed for this zone
   */
  color: string | undefined | null;
  /** Additional zones to highlight using the same colour while this zone is active. */
  additionalHighlightedZones?: ScaffoldDropZone[];
}

/**
 * Zone highlight configuration, either static per-zone colours or a dynamic resolver function.
 */
export type ScaffoldZoneColors =
  | Partial<Record<ScaffoldDropZone, string>>
  | ((params: IScaffoldZoneColorResolverParams) => IScaffoldZoneColorResolverResult);

/**
 * Ordered list of the sixteen rendered drop zones in the scaffold overlay grid.
 */
export const ZONE_GRID_ORDER: ScaffoldDropZone[] = [
  ScaffoldDropZone.TopLeft,
  ScaffoldDropZone.TopEdgeLeft,
  ScaffoldDropZone.TopEdgeRight,
  ScaffoldDropZone.TopRight,
  ScaffoldDropZone.LeftEdgeTop,
  ScaffoldDropZone.CentreTopLeft,
  ScaffoldDropZone.CentreTopRight,
  ScaffoldDropZone.RightEdgeTop,
  ScaffoldDropZone.LeftEdgeBottom,
  ScaffoldDropZone.CentreBottomLeft,
  ScaffoldDropZone.CentreBottomRight,
  ScaffoldDropZone.RightEdgeBottom,
  ScaffoldDropZone.BottomLeft,
  ScaffoldDropZone.BottomEdgeLeft,
  ScaffoldDropZone.BottomEdgeRight,
  ScaffoldDropZone.BottomRight,
];

/**
 * Zones belonging to the left or right column of the 4×4 grid.
 */
export const HORIZONTAL_SIDE_ZONES = new Set([
  ScaffoldDropZone.TopLeft,
  ScaffoldDropZone.LeftEdgeTop,
  ScaffoldDropZone.LeftEdgeBottom,
  ScaffoldDropZone.BottomLeft,
  ScaffoldDropZone.TopRight,
  ScaffoldDropZone.RightEdgeTop,
  ScaffoldDropZone.RightEdgeBottom,
  ScaffoldDropZone.BottomRight,
]);

/**
 * Zones belonging to the top or bottom row of the 4×4 grid.
 */
export const VERTICAL_SIDE_ZONES = new Set([
  ScaffoldDropZone.TopLeft,
  ScaffoldDropZone.TopEdgeLeft,
  ScaffoldDropZone.TopEdgeRight,
  ScaffoldDropZone.TopRight,
  ScaffoldDropZone.BottomLeft,
  ScaffoldDropZone.BottomEdgeLeft,
  ScaffoldDropZone.BottomEdgeRight,
  ScaffoldDropZone.BottomRight,
]);

/**
 * Positional bounds for a drop zone in the canonical 4×4 zone track grid.
 */
export interface IScaffoldZoneGridBounds {
  /** Starting row line in the canonical grid (1-based). */
  top: number;
  /** Ending row line in the canonical grid (1-based, exclusive). */
  bottom: number;
  /** Starting column line in the canonical grid (1-based). */
  left: number;
  /** Ending column line in the canonical grid (1-based, exclusive). */
  right: number;
}

/**
 * Canonical bounds for each drop zone in a 4×4 track grid.
 */
export const SCAFFOLD_ZONE_GRID_BOUNDS: Record<ScaffoldDropZone, IScaffoldZoneGridBounds> = {
  [ScaffoldDropZone.TopLeft]: { top: 1, bottom: 2, left: 1, right: 2 },
  [ScaffoldDropZone.TopEdgeLeft]: { top: 1, bottom: 2, left: 2, right: 3 },
  [ScaffoldDropZone.TopEdgeRight]: { top: 1, bottom: 2, left: 3, right: 4 },
  [ScaffoldDropZone.TopRight]: { top: 1, bottom: 2, left: 4, right: 5 },
  [ScaffoldDropZone.LeftEdgeTop]: { top: 2, bottom: 3, left: 1, right: 2 },
  [ScaffoldDropZone.CentreTopLeft]: { top: 2, bottom: 3, left: 2, right: 3 },
  [ScaffoldDropZone.CentreTopRight]: { top: 2, bottom: 3, left: 3, right: 4 },
  [ScaffoldDropZone.RightEdgeTop]: { top: 2, bottom: 3, left: 4, right: 5 },
  [ScaffoldDropZone.LeftEdgeBottom]: { top: 3, bottom: 4, left: 1, right: 2 },
  [ScaffoldDropZone.CentreBottomLeft]: { top: 3, bottom: 4, left: 2, right: 3 },
  [ScaffoldDropZone.CentreBottomRight]: { top: 3, bottom: 4, left: 3, right: 4 },
  [ScaffoldDropZone.RightEdgeBottom]: { top: 3, bottom: 4, left: 4, right: 5 },
  [ScaffoldDropZone.BottomLeft]: { top: 4, bottom: 5, left: 1, right: 2 },
  [ScaffoldDropZone.BottomEdgeLeft]: { top: 4, bottom: 5, left: 2, right: 3 },
  [ScaffoldDropZone.BottomEdgeRight]: { top: 4, bottom: 5, left: 3, right: 4 },
  [ScaffoldDropZone.BottomRight]: { top: 4, bottom: 5, left: 4, right: 5 },
};

/**
 * Default highlight colours for each drop zone, used when no custom zoneColors prop is supplied.
 * Corner zones use a stronger red, split edge zones use a lighter red, and centre quadrants use blue.
 */
export const DEFAULT_SCAFFOLD_ZONE_COLORS: Record<ScaffoldDropZone, string> = {
  [ScaffoldDropZone.TopLeft]: "#ff6b6b",
  [ScaffoldDropZone.TopEdgeLeft]: "#ff9999",
  [ScaffoldDropZone.TopEdgeRight]: "#ff9999",
  [ScaffoldDropZone.TopRight]: "#ff6b6b",
  [ScaffoldDropZone.LeftEdgeTop]: "#ff9999",
  [ScaffoldDropZone.CentreTopLeft]: "#4dabf7",
  [ScaffoldDropZone.CentreTopRight]: "#4dabf7",
  [ScaffoldDropZone.RightEdgeTop]: "#ff9999",
  [ScaffoldDropZone.LeftEdgeBottom]: "#ff9999",
  [ScaffoldDropZone.CentreBottomLeft]: "#4dabf7",
  [ScaffoldDropZone.CentreBottomRight]: "#4dabf7",
  [ScaffoldDropZone.RightEdgeBottom]: "#ff9999",
  [ScaffoldDropZone.BottomLeft]: "#ff6b6b",
  [ScaffoldDropZone.BottomEdgeLeft]: "#ff9999",
  [ScaffoldDropZone.BottomEdgeRight]: "#ff9999",
  [ScaffoldDropZone.BottomRight]: "#ff6b6b",
};

/**
 * Minimum column width in pixels to prevent collapsing during resize or animation. This is a safeguard and can be adjusted as needed.
 */
export const MIN_SCAFFOLD_COL_WIDTH_PX = 8;

/**
 * Minimum row height in pixels to prevent collapsing during resize or animation. This is a safeguard and can be adjusted as needed.
 */
export const MIN_SCAFFOLD_ROW_HEIGHT_PX = 8;

/**
 * Duration in milliseconds for scaffold enter/leave animations.
 */
export const SCAFFOLD_ANIMATION_DURATION_MS = 200;

/**
 * Delay in milliseconds before a scaffold enter animation starts, giving the browser a paint frame.
 */
export const SCAFFOLD_ANIMATION_START_DELAY_MS = 5;

/**
 *  Width/height in pixels of each non-centre drop zone.
 */
export const SIDE_ZONE_SIZE = 11;

/**
 *  Minimum container dimension in pixels required to render side zones along that axis.
 */
export const SIDE_ZONE_MIN_SIZE = SIDE_ZONE_SIZE * 2 + 2;

/**
 * Animation states for scaffold columns and rows, used to manage enter and leave animations.
 * "placed" state is used for newly added columns before the animation starts,
 * "opening" is during the enter animation,
 * "open" is the settled state,
 * "closing" is during the exit animation,
 * "closed" indicates the item has finished leaving and can be unmounted.
 */
export type ScaffoldAnimationState = "placed" | "opening" | "open" | "closing" | "closed";

/**
 * Selection state for the Scaffold, tracking selected cells, rows, and columns.
 */
export interface ScaffoldSelection {
  /** Selected cells grouped by row. Each entry contains a row ID and an array of selected column IDs in that row. */
  selectedCells: { rowId: string; colId: string[] }[];
  /** Selected rows, each identified by the row ID stored in firstCellId. */
  selectedRows: { firstCellId: string }[];
  /** Selected columns, each identified by the column ID stored in firstCellId. */
  selectedCols: { firstCellId: string }[];
}

/**
 * An empty selection state, suitable as a default or to clear an active selection.
 */
export const EMPTY_SCAFFOLD_SELECTION: ScaffoldSelection = {
  selectedCells: [],
  selectedRows: [],
  selectedCols: [],
};

/**
 * Width and height in pixels of the row and column selection handle strips.
 */
export const SCAFFOLD_SELECT_HANDLE_SIZE_PX = 12;

/**
 * Span definition for a single scaffold cell, allowing it to span multiple columns and/or rows.
 * Only the first (top-left) cell of the spanned region is rendered; all other cells in the
 * spanned region are suppressed from the layout.
 */
export interface ScaffoldHeaderCellSpan {
  /** Number of consecutive columns this cell spans (default 1). Must be ≥ 1. */
  colSpan?: number;
  /** Number of consecutive rows this cell spans (default 1). Must be ≥ 1. */
  rowSpan?: number;
}

/**
 * Maps row IDs to column IDs to span definitions, allowing individual scaffold cells to span
 * multiple columns and/or rows.  Only cells that deviate from the default (1×1) need entries.
 */
export type ScaffoldHeaderCellSpans = Record<string, Record<string, ScaffoldHeaderCellSpan>>;
